'use client'

import { useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { LobbyHeaderMeta } from '@/contexts/LobbyHeaderMetaContext'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  MagicWand01Icon,
  Shield01Icon,
  BalanceScaleIcon,
  PauseIcon,
  FilterIcon,
  UserGroup03Icon,
  SlidersHorizontalIcon,
  Route01Icon,
  Clock01Icon,
  Activity01Icon,
  BinocularsIcon,
  File01Icon,
  PlusSignIcon,
  SearchIcon,
  ModernTvIcon,
  HierarchyCircle03Icon,
  GridViewIcon,
  Building01Icon,
  Calendar03Icon,
  Calendar04Icon,
  Sun01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import type { IconSvgElement } from '@hugeicons/react'
import {
  AUTOMATION_CHANNEL_META,
  FALLBACK_SCHEDULES,
  type AutomationChannel,
  type CommandIconKey,
  type ScheduleRow,
} from '@/lib/automations/fallback-data'
import { SlideOutPanel } from '@/components/ui/slide-out-panel'
import { Skeleton } from '@/components/ui/skeleton'

const ROW_ICON: Record<CommandIconKey, IconSvgElement> = {
  sparkles: MagicWand01Icon,
  shield: Shield01Icon,
  balance: BalanceScaleIcon,
  pause: PauseIcon,
  filter: FilterIcon,
  users: UserGroup03Icon,
  sliders: SlidersHorizontalIcon,
  route: Route01Icon,
  clock: Clock01Icon,
  activity: Activity01Icon,
  binoculars: BinocularsIcon,
  file: File01Icon,
}

const CHANNEL_PILL_ICON: Record<AutomationChannel, IconSvgElement> = {
  search: SearchIcon,
  social: UserGroup03Icon,
  tv: ModernTvIcon,
  programmatic: HierarchyCircle03Icon,
  audio: HierarchyCircle03Icon,
  retail: HierarchyCircle03Icon,
  creative: MagicWand01Icon,
  analyst: Activity01Icon,
  workspace: Building01Icon,
  all: GridViewIcon,
}

const CADENCE_ICON: Record<string, IconSvgElement> = {
  hourly: Clock01Icon,
  daily: Sun01Icon,
  weekly: Calendar03Icon,
  monthly: Calendar04Icon,
  cron: Clock01Icon,
}

type CommandOption = {
  id: string
  name: string
  description: string
  prompt: string
  iconKey: CommandIconKey
  channel: AutomationChannel
}

type ScheduleView = {
  id: string
  key: string
  commandId: string
  name: string
  description: string
  prompt: string
  iconKey: CommandIconKey
  channel: AutomationChannel
  cadence: string
  cadenceValue: string
  cadenceConfig: Record<string, unknown>
  nextRun: string
  enabled: boolean
}

type RunView = {
  id: string
  commandName: string
  iconKey: CommandIconKey
  channel: AutomationChannel
  triggeredBy: string
  status: string
  createdAt: string
  durationMs: number | null
  prompt: string
  response: string
  version: number
  currentVersion: number
}

type ScheduleForm = {
  id?: string
  command_id: string
  cadence: string
  time: string
  timezone: string
  enabled: boolean
  prompt: string
}

function relativeTime(value: string | null | undefined) {
  if (!value) return 'Never'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const diff = Date.now() - date.getTime()
  const minutes = Math.round(Math.abs(diff) / 60000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return `${days}d ago`
}

function fallbackRows(schedules: ScheduleRow[]): ScheduleView[] {
  return schedules.map((schedule) => ({
    id: schedule.key,
    key: schedule.key,
    commandId: schedule.key,
    name: schedule.name,
    description: '',
    prompt: '',
    iconKey: schedule.iconKey,
    channel: schedule.channel,
    cadence: schedule.cadence,
    cadenceValue: 'weekly',
    cadenceConfig: {},
    nextRun: schedule.nextRun,
    enabled: schedule.enabled,
  }))
}

function commandFromApi(row: any): CommandOption {
  return {
    id: row.id,
    name: row.name,
    description: row.description || '',
    prompt: row.prompt || '',
    iconKey: row.icon_key || 'sparkles',
    channel: row.channel || 'workspace',
  }
}

function scheduleFromApi(row: any): ScheduleView {
  const command = Array.isArray(row.commands) ? row.commands[0] : row.commands
  return {
    id: row.id,
    key: row.id,
    commandId: row.command_id,
    name: command?.name || 'Command',
    description: command?.description || '',
    prompt: command?.prompt || '',
    iconKey: command?.icon_key || 'sparkles',
    channel: command?.channel || 'workspace',
    cadence: cadenceLabel(row.cadence, row.cadence_config || {}),
    cadenceValue: row.cadence,
    cadenceConfig: row.cadence_config || {},
    nextRun: row.next_run_at ? new Date(row.next_run_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Not calculated',
    enabled: row.enabled,
  }
}

function runFromApi(row: any): RunView {
  const command = Array.isArray(row.commands) ? row.commands[0] : row.commands
  const version = Array.isArray(row.command_versions) ? row.command_versions[0] : row.command_versions
  return {
    id: row.id,
    commandName: command?.name || 'Command',
    iconKey: command?.icon_key || 'sparkles',
    channel: command?.channel || 'workspace',
    triggeredBy: row.triggered_by || 'manual',
    status: row.status || 'queued',
    createdAt: row.created_at,
    durationMs: row.duration_ms,
    prompt: version?.prompt || '',
    response: row.response || row.error || '',
    version: version?.version || 1,
    currentVersion: command?.current_version || version?.version || 1,
  }
}

function cadenceLabel(cadence: string, config: Record<string, unknown>) {
  const time = typeof config.time === 'string' ? config.time : ''
  const timezone = typeof config.timezone === 'string' ? config.timezone : ''
  const base = cadence.charAt(0).toUpperCase() + cadence.slice(1)
  return [base, time, timezone].filter(Boolean).join(' · ')
}

function ScheduleRowSkeleton() {
  return (
    <div className="bv3-schrow">
      <Skeleton className="schic size-11 rounded-xl" />
      <div className="schmain min-w-0 flex-1 space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-3 w-32" />
      </div>
      <div className="schright flex flex-wrap items-center gap-3">
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-6 w-11 rounded-full" />
        <Skeleton className="h-8 w-14 rounded-lg" />
      </div>
    </div>
  )
}

function RunRowSkeleton() {
  return (
    <div className="flex items-center gap-3 border-b border-line px-4 py-3 last:border-b-0">
      <Skeleton className="size-9 rounded-lg" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-28" />
      </div>
      <Skeleton className="h-6 w-16 rounded-full" />
      <Skeleton className="h-6 w-20 rounded-full" />
    </div>
  )
}

function ScheduleModal({
  form,
  commands,
  saving,
  onChange,
  onClose,
  onSubmit,
}: {
  form: ScheduleForm
  commands: CommandOption[]
  saving: boolean
  onChange: (patch: Partial<ScheduleForm>) => void
  onClose: () => void
  onSubmit: () => void
}) {
  const selected = commands.find((command) => command.id === form.command_id)
  const channel = selected?.channel || 'workspace'
  const iconKey = selected?.iconKey || 'clock'

  return (
    <SlideOutPanel
      open
      onOpenChange={(open) => !open && onClose()}
      direction="right"
      className="md:max-w-xl"
      title={form.id ? 'Edit Schedule' : 'New Schedule'}
    >
      <div className="p-6 pt-4">
        <p className="mb-5 text-sm text-text">Choose timing and update the command prompt when needed.</p>

        <div className="space-y-4">
          <label className="block text-sm font-medium text-atrium-black">
            Command
            <select className="mt-1 w-full rounded-xl border border-atrium-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#006241]/20" value={form.command_id} onChange={(e) => {
              const command = commands.find((item) => item.id === e.target.value)
              onChange({ command_id: e.target.value, prompt: command?.prompt || '' })
            }}>
              <option value="">Select a command</option>
              {commands.map((command) => <option key={command.id} value={command.id}>{command.name}</option>)}
            </select>
          </label>

          {selected && (
            <div className="flex items-center gap-3 rounded-xl border border-atrium-border bg-atrium-content p-3">
              <span className="grid size-10 place-items-center rounded-xl text-white" style={{ background: AUTOMATION_CHANNEL_META[channel].gradient }}>
                <HugeiconsIcon icon={ROW_ICON[iconKey]} className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-atrium-black">{selected.name}</div>
                <div className="truncate text-xs text-atrium-black/50">{selected.description}</div>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold" style={{ background: AUTOMATION_CHANNEL_META[channel].tint, color: AUTOMATION_CHANNEL_META[channel].ink }}>
                <HugeiconsIcon icon={CHANNEL_PILL_ICON[channel]} className="size-3" />
                {AUTOMATION_CHANNEL_META[channel].label}
              </span>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            {['hourly', 'daily', 'weekly', 'monthly'].map((cadence) => (
              <button key={cadence} type="button" className={`rounded-xl border px-3 py-3 text-left text-sm ${form.cadence === cadence ? 'border-[#006241] bg-emerald-50 text-[#006241]' : 'border-atrium-border text-atrium-black hover:bg-atrium-content'}`} onClick={() => onChange({ cadence })}>
                <HugeiconsIcon icon={CADENCE_ICON[cadence]} className="mb-2 size-5" />
                {cadence.charAt(0).toUpperCase() + cadence.slice(1)}
              </button>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-medium text-atrium-black">
              Time
              <input type="time" className="mt-1 w-full rounded-xl border border-atrium-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#006241]/20" value={form.time} onChange={(e) => onChange({ time: e.target.value })} />
            </label>
            <label className="block text-sm font-medium text-atrium-black">
              Timezone
              <select className="mt-1 w-full rounded-xl border border-atrium-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#006241]/20" value={form.timezone} onChange={(e) => onChange({ timezone: e.target.value })}>
                {['America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'UTC'].map((zone) => <option key={zone} value={zone}>{zone}</option>)}
              </select>
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm font-medium text-atrium-black">
            <input type="checkbox" checked={form.enabled} onChange={(e) => onChange({ enabled: e.target.checked })} />
            Enabled
          </label>

          <label className="block text-sm font-medium text-atrium-black">
            Command prompt
            <textarea className="mt-1 min-h-44 w-full resize-y rounded-xl border border-atrium-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#006241]/20" value={form.prompt} onChange={(e) => onChange({ prompt: e.target.value })} />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="rounded-xl border border-atrium-border px-4 py-2 text-sm font-medium text-atrium-black hover:bg-atrium-content" onClick={onClose}>Cancel</button>
          <button type="button" className="bv3-btn-grn" style={{ padding: '8px 14px', fontSize: 13 }} disabled={saving || !form.command_id} onClick={onSubmit}>{saving ? 'Saving...' : 'Save schedule'}</button>
        </div>
      </div>
    </SlideOutPanel>
  )
}

export function ScheduledV3Page({ schedules }: { schedules: ScheduleRow[] }) {
  const [rows, setRows] = useState<ScheduleView[]>(() => fallbackRows(schedules.length ? schedules : FALLBACK_SCHEDULES))
  const [commands, setCommands] = useState<CommandOption[]>([])
  const [runs, setRuns] = useState<RunView[]>([])
  const [loaded, setLoaded] = useState(false)
  const [form, setForm] = useState<ScheduleForm | null>(null)
  const [saving, setSaving] = useState(false)
  const [selectedRun, setSelectedRun] = useState<RunView | null>(null)
  const activeCount = useMemo(() => rows.filter((r) => r.enabled).length, [rows])

  async function load() {
    const [commandsRes, schedulesRes, runsRes] = await Promise.all([
      fetch('/api/commands'),
      fetch('/api/schedules'),
      fetch('/api/commands/runs/recent?limit=20'),
    ])
    if (commandsRes.ok) {
      const data = await commandsRes.json()
      setCommands((data.commands || []).map(commandFromApi))
    }
    if (schedulesRes.ok) {
      const data = await schedulesRes.json()
      setRows((data.schedules || []).map(scheduleFromApi))
    }
    if (runsRes.ok) {
      const data = await runsRes.json()
      setRuns((data.runs || []).map(runFromApi))
    }
    setLoaded(true)
  }

  useEffect(() => { void load() }, [])

  async function toggle(schedule: ScheduleView) {
    setRows((prev) => prev.map((row) => row.id === schedule.id ? { ...row, enabled: !row.enabled } : row))
    const res = await fetch(`/api/schedules/${schedule.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !schedule.enabled }),
    })
    if (!res.ok) {
      alert(await res.text())
      await load()
    }
  }

  function openNewSchedule() {
    setForm({
      command_id: commands[0]?.id || '',
      cadence: 'daily',
      time: '08:00',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
      enabled: true,
      prompt: commands[0]?.prompt || '',
    })
  }

  function openEditSchedule(schedule: ScheduleView) {
    setForm({
      id: schedule.id,
      command_id: schedule.commandId,
      cadence: schedule.cadenceValue,
      time: String(schedule.cadenceConfig.time || '08:00'),
      timezone: String(schedule.cadenceConfig.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'),
      enabled: schedule.enabled,
      prompt: schedule.prompt,
    })
  }

  async function saveSchedule() {
    if (!form) return
    setSaving(true)
    const selected = commands.find((command) => command.id === form.command_id)
    if (selected && form.prompt !== selected.prompt) {
      const commandRes = await fetch(`/api/commands/${selected.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: form.prompt }),
      })
      if (!commandRes.ok) {
        setSaving(false)
        alert(await commandRes.text())
        return
      }
    }
    const url = form.id ? `/api/schedules/${form.id}` : '/api/schedules'
    const method = form.id ? 'PATCH' : 'POST'
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        command_id: form.command_id,
        cadence: form.cadence,
        cadence_config: { time: form.time, timezone: form.timezone },
        enabled: form.enabled,
      }),
    })
    setSaving(false)
    if (!res.ok) {
      alert(await res.text())
      return
    }
    setForm(null)
    await load()
  }

  return (
    <div className="bv3-canvas min-h-full">
      <LobbyHeaderMeta>{activeCount} active · {rows.length} automations</LobbyHeaderMeta>

      <div className="bv3-au py-8">
        <div className="bv3-au-head">
          <h1>Scheduled</h1>
          <button type="button" className="bv3-btn-grn" style={{ padding: '8px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }} onClick={openNewSchedule}>
            <HugeiconsIcon icon={PlusSignIcon} className="size-4" />
            New schedule
          </button>
        </div>

        <div style={{ marginTop: 16 }}>
          {!loaded ? (
            Array.from({ length: 4 }).map((_, index) => <ScheduleRowSkeleton key={index} />)
          ) : rows.length === 0 ? (
            <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-100 p-10 text-center">
              <div className="mx-auto mb-4 grid size-16 place-items-center rounded-2xl bg-white text-[#006241] shadow-sm">
                <HugeiconsIcon icon={Clock01Icon} className="size-9" />
              </div>
              <h2 className="text-2xl font-bold text-atrium-black">No scheduled commands yet</h2>
              <p className="mx-auto mt-2 max-w-lg text-sm text-atrium-black/60">When you schedule a command, it will show up here with its next run time and a paper trail of every execution.</p>
              <button type="button" className="bv3-btn-grn mx-auto mt-5" style={{ padding: '8px 14px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }} onClick={openNewSchedule}>
                <HugeiconsIcon icon={PlusSignIcon} className="size-4" />
                Schedule your first command
              </button>
            </div>
          ) : rows.map((s) => {
            const meta = AUTOMATION_CHANNEL_META[s.channel]
            return (
              <div key={s.key} className={`bv3-schrow ${s.enabled ? '' : 'off'}`}>
                <div className="schic">
                  <HugeiconsIcon icon={ROW_ICON[s.iconKey]} className="size-5" />
                </div>
                <div className="schmain">
                  <div className="schnm">{s.name}</div>
                  <div className="schcad">{s.cadence}</div>
                </div>
                <div className="schright">
                  <span className="chtag" style={{ background: meta.tint, color: meta.ink, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                    <HugeiconsIcon icon={CHANNEL_PILL_ICON[s.channel]} className="size-3" />
                    {meta.label}
                  </span>
                  <div className="schnext">
                    <div className="lbl">Next run</div>
                    <div className="v">{s.enabled ? s.nextRun : 'Paused'}</div>
                  </div>
                  <label className="bv3-sw">
                    <input type="checkbox" checked={s.enabled} onChange={() => void toggle(s)} aria-label={`${s.enabled ? 'Pause' : 'Resume'} ${s.name}`} />
                    <span className="track" />
                  </label>
                  <button type="button" className="bv3-btn-light" onClick={() => openEditSchedule(s)}>Edit</button>
                </div>
              </div>
            )
          })}
        </div>

        <section className="mt-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-muted">Recent runs</h2>
          {!loaded ? (
            <div className="overflow-hidden rounded-2xl border border-line bg-surface">
              {Array.from({ length: 5 }).map((_, index) => (
                <RunRowSkeleton key={index} />
              ))}
            </div>
          ) : runs.length === 0 ? (
            <div className="rounded-2xl border border-line bg-surface p-6 text-center">
              <div className="mx-auto mb-3 grid size-12 place-items-center rounded-xl bg-surface-2 text-brand shadow-sm">
                <HugeiconsIcon icon={File01Icon} className="size-6" />
              </div>
              <h3 className="text-lg font-bold text-text-strong">No commands have run yet</h3>
              <p className="mt-1 text-sm text-text-muted-2">Every manual and scheduled execution will appear here.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-line bg-surface">
              {runs.map((run) => (
                <button key={run.id} type="button" className="flex w-full items-center gap-3 border-b border-line px-4 py-3 text-left last:border-b-0 hover:bg-canvas" onClick={() => setSelectedRun(run)}>
                  <span className="grid size-9 place-items-center rounded-lg text-white" style={{ background: AUTOMATION_CHANNEL_META[run.channel].gradient }}>
                    <HugeiconsIcon icon={ROW_ICON[run.iconKey]} className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-text-strong">{run.commandName}</span>
                    <span className="text-xs text-text-muted-2">{relativeTime(run.createdAt)} · {run.durationMs ? `${run.durationMs}ms` : 'pending'}</span>
                  </span>
                  <span className="rounded-full bg-surface-2 px-2 py-1 text-xs font-semibold capitalize text-text-muted-2">{run.triggeredBy}</span>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${run.status === 'succeeded' ? 'bg-green-100 text-green-700' : run.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{run.status}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {form && <ScheduleModal form={form} commands={commands} saving={saving} onChange={(patch) => setForm((prev) => prev ? { ...prev, ...patch } : prev)} onClose={() => setForm(null)} onSubmit={() => void saveSchedule()} />}
      {selectedRun && (
        <SlideOutPanel
          open
          onOpenChange={(open) => !open && setSelectedRun(null)}
          direction="right"
          className="md:max-w-xl"
          title={selectedRun.commandName}
        >
          <div className="p-6 pt-4">
            <div className="mb-4 flex items-center gap-2 text-xs text-text">
              <span>Version {selectedRun.version}</span>
              {selectedRun.version !== selectedRun.currentVersion && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">
                  Pinned old version
                </span>
              )}
            </div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-atrium-black/40">Pinned prompt</h3>
            <pre className="mb-5 max-h-52 overflow-auto whitespace-pre-wrap rounded-xl border border-atrium-border bg-atrium-content p-3 text-xs text-atrium-black/70">{selectedRun.prompt}</pre>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-atrium-black/40">Response</h3>
            <div className="prose prose-sm max-w-none text-atrium-black">
              <ReactMarkdown>{selectedRun.response || 'No response yet.'}</ReactMarkdown>
            </div>
          </div>
        </SlideOutPanel>
      )}
    </div>
  )
}
